import {
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";
import { getDeliveredAtDateFromIAContext } from "../../utils/getDeliveredAtDateFromIAContext";
import { orderCanBeReportedBasedOnDeliveryDate } from "../../utils/orderCanBeReportedBasedOnDeliveryDate";

import {
  DO_NOT_PROCESS_DAMAGED_AS_RETURN,
  INELIGIBLE_FOR_REPORT,
  NEED_MORE_INFO_ANSWER,
  getReportItemsOrRequestExtensionCTA,
} from "./shared";

export const ReportDamagedItemIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getReportItemsOrRequestExtensionCTA(context, strictLocalize)
          ?.label;
      },
      linkBuilder: (context) => {
        return getReportItemsOrRequestExtensionCTA(
          context,
          strictLocalize
        )?.getLink?.(context.brandOrder);
      },
    },
    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },
    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder, shippingContext } = context;
  const deliveredAt = getDeliveredAtDateFromIAContext(context);
  if (!brandOrder || !shippingContext || !deliveredAt) {
    return NEED_MORE_INFO_ANSWER(strictLocalize);
  }

  return orderCanBeReportedBasedOnDeliveryDate(deliveredAt)
    ? DAMAGE_ITEM_REPORT_ELIGIBLE(strictLocalize)
    : DAMAGE_ITEM_REPORT_INELIGIBLE(strictLocalize);
};

const DAMAGE_ITEM_REPORT_ELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => REPORT_DAMAGED(strictLocalize),
  getDescription: () => [USE_BUTTON_TO_REPORT_DAMAGE(strictLocalize)],
});

const DAMAGE_ITEM_REPORT_INELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => INELIGIBLE_FOR_REPORT(strictLocalize),
  getDescription: () => [
    REQUEST_EXCEPTIONAL_EXTENSION(strictLocalize),
    DO_NOT_PROCESS_DAMAGED_AS_RETURN(strictLocalize),
  ],
});

const REPORT_DAMAGED = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Report damaged, missing, or incorrect items",
    description: {
      text: "Title for the answer of report a damage item or shipment issue if the selected order delivery is within 14 days",
    },
  });

const USE_BUTTON_TO_REPORT_DAMAGE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "You can file a damaged item report using the button below.",
  });

const REQUEST_EXCEPTIONAL_EXTENSION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If there are exceptional circumstances, you can request an extension using the button below.",
  });
